import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertCircle,
  CheckCircle2,
  FileText,
  Globe,
  Loader2,
  RefreshCw,
  Search,
  Settings,
  Zap,
  Building2,
  CheckCircle,
  XCircle,
  HelpCircle,
  Plus,
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { PeppolAPI } from '@/services/peppol';
import {
  PeppolConfig,
  PeppolClient,
  PeppolCurrentSender,
  PeppolSenderParticipant,
  PeppolFirmDefaults,
  PeppolCreateSenderRequest,
  getSchemeInfo,
} from '@/types/peppol';
import { PeppolDocuments } from './PeppolDocuments';

interface PeppolSettingsProps {
  firmId: string | number;
  canModify?: boolean;
}

export function PeppolSettings({ firmId, canModify = true }: PeppolSettingsProps) {
  const [config, setConfig] = useState<PeppolConfig | null>(null);
  const [peppolClients, setPeppolClients] = useState<PeppolClient[]>([]);
  const [currentSender, setCurrentSender] = useState<PeppolCurrentSender | null>(null);
  const [senderParticipants, setSenderParticipants] = useState<PeppolSenderParticipant[]>([]);
  const [loading, setLoading] = useState(true);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'success' | 'error' | null>(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const [settingDefaultSender, setSettingDefaultSender] = useState(false);

  // Client lookup states
  const [lookingUpClients, setLookingUpClients] = useState(false);
  const [lookingUpClientId, setLookingUpClientId] = useState<number | null>(null);

  // Sender creation states
  const [createSenderDialogOpen, setCreateSenderDialogOpen] = useState(false);
  const [firmDefaults, setFirmDefaults] = useState<PeppolFirmDefaults | null>(null);
  const [loadingFirmDefaults, setLoadingFirmDefaults] = useState(false);
  const [creatingSender, setCreatingSender] = useState(false);
  const [senderFormData, setSenderFormData] = useState<PeppolCreateSenderRequest>({
    street: '',
    street_number: '',
    zip_code: '',
    city: '',
    country: '',
    set_as_default: true,
  });

  useEffect(() => {
    fetchData();
  }, [firmId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [configData, clientsData, senderData, participantsData] = await Promise.all([
        PeppolAPI.getConfig(),
        PeppolAPI.getPeppolClients().catch(() => []),
        PeppolAPI.getCurrentSender().catch(() => null),
        PeppolAPI.getSenderParticipants().catch(() => []),
      ]);
      setConfig(configData);
      setPeppolClients(clientsData);
      setCurrentSender(senderData);
      setSenderParticipants(participantsData);
    } catch (error) {
      console.error('Failed to fetch Peppol data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load Peppol settings',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLookupClient = async (clientId: number) => {
    setLookingUpClientId(clientId);
    try {
      const result = await PeppolAPI.lookupClient(clientId);
      // Update the client in the list with new lookup status
      setPeppolClients(prev =>
        prev.map(c =>
          c.id === clientId
            ? { ...c, lookup_status: result.exists ? 'verified' : 'not_found', last_lookup: new Date().toISOString() }
            : c
        )
      );
      toast({
        title: result.exists ? 'Client Verified' : 'Client Not Found',
        description: result.exists
          ? 'Client is registered on the Peppol network'
          : 'Client is not registered on the Peppol network',
        variant: result.exists ? 'default' : 'destructive',
      });
    } catch (error) {
      toast({
        title: 'Lookup Failed',
        description: 'Failed to lookup client on Peppol network',
        variant: 'destructive',
      });
    } finally {
      setLookingUpClientId(null);
    }
  };

  const handleLookupAllClients = async () => {
    setLookingUpClients(true);
    try {
      const result = await PeppolAPI.lookupAllClients();
      toast({
        title: 'Lookup Complete',
        description: `Verified: ${result.verified}, Not found: ${result.not_found}, Failed: ${result.failed}`,
      });
      // Refresh client list
      const clientsData = await PeppolAPI.getPeppolClients().catch(() => []);
      setPeppolClients(clientsData);
    } catch (error) {
      toast({
        title: 'Lookup Failed',
        description: 'Failed to lookup clients on Peppol network',
        variant: 'destructive',
      });
    } finally {
      setLookingUpClients(false);
    }
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    setConnectionStatus(null);
    try {
      const result = await PeppolAPI.testConnection();
      setConnectionStatus(result.success ? 'success' : 'error');
      toast({
        title: result.success ? 'Connection Successful' : 'Connection Failed',
        description: result.message,
        variant: result.success ? 'default' : 'destructive',
      });
    } catch (error) {
      setConnectionStatus('error');
      toast({
        title: 'Connection Failed',
        description: 'Could not connect to Peppol network',
        variant: 'destructive',
      });
    } finally {
      setTestingConnection(false);
    }
  };

  const handleToggleEnabled = async (enabled: boolean) => {
    if (!canModify) return;
    setSavingConfig(true);
    try {
      const updated = await PeppolAPI.updateConfig({ is_enabled: enabled });
      setConfig(updated);
      toast({
        title: 'Settings Updated',
        description: `Peppol integration ${enabled ? 'enabled' : 'disabled'}`,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update Peppol settings',
        variant: 'destructive',
      });
    } finally {
      setSavingConfig(false);
    }
  };

  const handleSetDefaultSender = async (participantId: string) => {
    if (!canModify) return;
    setSettingDefaultSender(true);
    try {
      const id = participantId === 'none' ? null : parseInt(participantId, 10);
      const updated = await PeppolAPI.updateConfig({ default_participant_id: id });
      setConfig(updated);
      // Refresh current sender to reflect the change
      const senderData = await PeppolAPI.getCurrentSender().catch(() => null);
      setCurrentSender(senderData);
      toast({
        title: 'Default Sender Updated',
        description: id ? 'Default sender has been set' : 'Default sender has been cleared',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update default sender',
        variant: 'destructive',
      });
    } finally {
      setSettingDefaultSender(false);
    }
  };

  const handleOpenCreateSenderDialog = async () => {
    setCreateSenderDialogOpen(true);
    setLoadingFirmDefaults(true);
    try {
      const defaults = await PeppolAPI.getFirmDefaults();
      setFirmDefaults(defaults);
      // Pre-fill country from firm defaults
      setSenderFormData((prev) => ({
        ...prev,
        country: defaults.country || '',
      }));
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load firm defaults. Your firm may not have a VAT number configured.',
        variant: 'destructive',
      });
      setCreateSenderDialogOpen(false);
    } finally {
      setLoadingFirmDefaults(false);
    }
  };

  const handleCreateSender = async () => {
    // Validate required fields
    if (!senderFormData.street || !senderFormData.street_number || !senderFormData.zip_code || !senderFormData.city || !senderFormData.country) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required address fields',
        variant: 'destructive',
      });
      return;
    }

    setCreatingSender(true);
    try {
      await PeppolAPI.createSenderFromFirm(senderFormData);
      toast({
        title: 'Sender Created',
        description: 'Your Peppol sender identity has been created and registered',
      });
      setCreateSenderDialogOpen(false);
      // Reset form
      setSenderFormData({
        street: '',
        street_number: '',
        zip_code: '',
        city: '',
        country: '',
        set_as_default: true,
      });
      // Refresh data
      await fetchData();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create sender identity';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setCreatingSender(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <Tabs defaultValue="configuration" className="space-y-6">
      <TabsList>
        <TabsTrigger value="configuration" className="flex items-center gap-2">
          <Settings className="h-4 w-4" />
          Configuration
        </TabsTrigger>
        <TabsTrigger value="clients" className="flex items-center gap-2">
          <Building2 className="h-4 w-4" />
          Clients
        </TabsTrigger>
        <TabsTrigger value="documents" className="flex items-center gap-2">
          <FileText className="h-4 w-4" />
          Documents
        </TabsTrigger>
      </TabsList>

      <TabsContent value="configuration" className="space-y-6">
        {/* Configuration Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                <CardTitle>Peppol Configuration</CardTitle>
              </div>
              <Badge variant={config?.is_enabled ? 'default' : 'secondary'}>
                {config?.is_enabled ? 'Enabled' : 'Disabled'}
              </Badge>
            </div>
            <CardDescription>
              Configure Peppol e-invoicing integration for electronic document exchange
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Enable/Disable Toggle */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="peppol-enabled">Enable Peppol Integration</Label>
                <p className="text-sm text-muted-foreground">
                  Send invoices electronically via the Peppol network
                </p>
              </div>
              <Switch
                id="peppol-enabled"
                checked={config?.is_enabled || false}
                onCheckedChange={handleToggleEnabled}
                disabled={!canModify || savingConfig}
              />
            </div>

            <Separator />

            {/* Environment Info */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Environment</Label>
                <p className="text-sm text-muted-foreground">
                  Current Peppol network environment
                </p>
              </div>
              <Badge variant="outline">
                {config?.environment?.toUpperCase() || 'TEST'}
              </Badge>
            </div>

            <Separator />

            {/* Connection Test */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Connection Status</Label>
                <p className="text-sm text-muted-foreground">
                  Verify connection to Peppol network
                </p>
              </div>
              <div className="flex items-center gap-2">
                {connectionStatus === 'success' && (
                  <Badge variant="default" className="bg-green-600">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Connected
                  </Badge>
                )}
                {connectionStatus === 'error' && (
                  <Badge variant="destructive">
                    <AlertCircle className="h-3 w-3 mr-1" />
                    Failed
                  </Badge>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleTestConnection}
                  disabled={testingConnection}
                >
                  {testingConnection ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Zap className="h-4 w-4 mr-2" />
                  )}
                  Test Connection
                </Button>
              </div>
            </div>

            <Separator />

            {/* Default Sender Selection */}
            <div className="space-y-3">
              <div className="space-y-0.5">
                <Label>Default Sender</Label>
                <p className="text-sm text-muted-foreground">
                  Select the default sender identity for Peppol invoices
                </p>
              </div>
              {senderParticipants.length > 0 ? (
                <div className="flex gap-2 items-start">
                  <Select
                    value={currentSender?.sender?.id?.toString() || 'none'}
                    onValueChange={handleSetDefaultSender}
                    disabled={!canModify || settingDefaultSender}
                  >
                    <SelectTrigger className="w-full max-w-md">
                      {settingDefaultSender ? (
                        <div className="flex items-center">
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Updating...
                        </div>
                      ) : (
                        <SelectValue placeholder="Select a sender..." />
                      )}
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No default sender</SelectItem>
                      {senderParticipants
                        .filter((p) => p.is_active && p.is_registered)
                        .map((participant) => (
                          <SelectItem key={participant.id} value={participant.id.toString()}>
                            <div className="flex flex-col">
                              <span>{participant.name}</span>
                              <span className="text-xs text-muted-foreground font-mono">
                                {participant.peppol_identifier}
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  {canModify && (
                    <Button variant="outline" onClick={handleOpenCreateSenderDialog} className="gap-2">
                      <Plus className="h-4 w-4" />
                      New
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>No Senders Available</AlertTitle>
                    <AlertDescription>
                      No registered sender participants are available. You can create one using your firm's VAT number.
                    </AlertDescription>
                  </Alert>
                  {canModify && (
                    <Button onClick={handleOpenCreateSenderDialog} className="gap-2">
                      <Plus className="h-4 w-4" />
                      Create Sender Identity
                    </Button>
                  )}
                </div>
              )}
            </div>

            <Separator />

            {/* Current Sender Info */}
            <div className="space-y-2">
              <Label>Current Sender Identity</Label>
              <p className="text-sm text-muted-foreground mb-3">
                Your firm's active identity when sending invoices via Peppol
              </p>
              {currentSender?.configured && currentSender.sender ? (
                <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                  <div>
                    <p className="font-medium">{currentSender.sender.name}</p>
                    <p className="text-sm text-muted-foreground font-mono">
                      {currentSender.sender.peppol_identifier}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Badge variant={currentSender.sender.is_registered ? 'default' : 'secondary'}>
                      {currentSender.sender.is_registered ? 'Registered' : 'Not Registered'}
                    </Badge>
                    <Badge variant={currentSender.sender.is_active ? 'default' : 'outline'}>
                      {currentSender.sender.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </div>
              ) : (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>No Sender Configured</AlertTitle>
                  <AlertDescription>
                    Select a default sender from the dropdown above, or contact your administrator.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="clients" className="space-y-6">
        {/* Peppol Clients Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                <CardTitle>Peppol-Enabled Clients</CardTitle>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleLookupAllClients}
                  disabled={lookingUpClients || peppolClients.length === 0}
                >
                  {lookingUpClients ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Search className="h-4 w-4 mr-2" />
                  )}
                  Verify All
                </Button>
                <Button variant="outline" size="sm" onClick={fetchData}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </div>
            <CardDescription>
              Clients configured to receive invoices via the Peppol network
            </CardDescription>
          </CardHeader>
          <CardContent>
            {peppolClients.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No Peppol-enabled clients found</p>
                <p className="text-sm mt-2">
                  Enable Peppol for clients in the Clients section to see them here
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Client</TableHead>
                    <TableHead>Peppol Identifier</TableHead>
                    <TableHead>Scheme</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Last Verified</TableHead>
                    <TableHead className="w-[100px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {peppolClients.map((client) => (
                    <TableRow key={client.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{client.name}</p>
                          {client.email && (
                            <p className="text-sm text-muted-foreground">{client.email}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {client.peppol_identifier || client.peppol_id || '-'}
                      </TableCell>
                      <TableCell>
                        {client.peppol_scheme ? (
                          <Badge variant="outline">
                            {client.scheme_display || getSchemeInfo(client.peppol_scheme).name}
                          </Badge>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        {client.lookup_status === 'verified' ? (
                          <Badge className="bg-green-600">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Verified
                          </Badge>
                        ) : client.lookup_status === 'not_found' ? (
                          <Badge variant="destructive">
                            <XCircle className="h-3 w-3 mr-1" />
                            Not Found
                          </Badge>
                        ) : (
                          <Badge variant="outline">
                            <HelpCircle className="h-3 w-3 mr-1" />
                            Not Verified
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {client.last_lookup
                          ? new Date(client.last_lookup).toLocaleDateString()
                          : 'Never'}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleLookupClient(client.id)}
                          disabled={lookingUpClientId === client.id}
                        >
                          {lookingUpClientId === client.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Search className="h-4 w-4" />
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="documents">
        <PeppolDocuments firmId={firmId} />
      </TabsContent>

      {/* Create Sender Dialog */}
      <Dialog open={createSenderDialogOpen} onOpenChange={setCreateSenderDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Peppol Sender Identity</DialogTitle>
            <DialogDescription>
              Create a new sender identity using your firm's VAT number to send invoices via Peppol.
            </DialogDescription>
          </DialogHeader>

          {loadingFirmDefaults ? (
            <div className="flex justify-center items-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : firmDefaults ? (
            <div className="space-y-4">
              {/* Firm Info Display */}
              <div className="bg-muted p-4 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Company</span>
                  <span className="font-medium">{firmDefaults.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">VAT Number</span>
                  <span className="font-mono">{firmDefaults.vat_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Peppol ID</span>
                  <span className="font-mono">{firmDefaults.suggested_scheme}:{firmDefaults.vat_number}</span>
                </div>
              </div>

              {!firmDefaults.has_vat_number && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>VAT Number Required</AlertTitle>
                  <AlertDescription>
                    Your firm must have a VAT number configured to create a Peppol sender identity.
                  </AlertDescription>
                </Alert>
              )}

              {/* Address Fields */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">Address Information</Label>
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2">
                    <Label htmlFor="street" className="text-xs text-muted-foreground">Street *</Label>
                    <Input
                      id="street"
                      value={senderFormData.street}
                      onChange={(e) => setSenderFormData({ ...senderFormData, street: e.target.value })}
                      placeholder="Street name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="street_number" className="text-xs text-muted-foreground">Number *</Label>
                    <Input
                      id="street_number"
                      value={senderFormData.street_number}
                      onChange={(e) => setSenderFormData({ ...senderFormData, street_number: e.target.value })}
                      placeholder="123"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label htmlFor="zip_code" className="text-xs text-muted-foreground">Postal Code *</Label>
                    <Input
                      id="zip_code"
                      value={senderFormData.zip_code}
                      onChange={(e) => setSenderFormData({ ...senderFormData, zip_code: e.target.value })}
                      placeholder="1000"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="city" className="text-xs text-muted-foreground">City *</Label>
                    <Input
                      id="city"
                      value={senderFormData.city}
                      onChange={(e) => setSenderFormData({ ...senderFormData, city: e.target.value })}
                      placeholder="Brussels"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="country" className="text-xs text-muted-foreground">Country Code *</Label>
                  <Input
                    id="country"
                    value={senderFormData.country}
                    onChange={(e) => setSenderFormData({ ...senderFormData, country: e.target.value.toUpperCase() })}
                    placeholder="BE"
                    maxLength={2}
                    className="w-20"
                  />
                </div>
              </div>

              {/* Set as Default Option */}
              <div className="flex items-center justify-between pt-2">
                <div className="space-y-0.5">
                  <Label htmlFor="set_as_default">Set as Default Sender</Label>
                  <p className="text-xs text-muted-foreground">
                    Use this identity as the default for sending invoices
                  </p>
                </div>
                <Switch
                  id="set_as_default"
                  checked={senderFormData.set_as_default}
                  onCheckedChange={(checked) => setSenderFormData({ ...senderFormData, set_as_default: checked })}
                />
              </div>
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateSenderDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateSender}
              disabled={creatingSender || loadingFirmDefaults || !firmDefaults?.has_vat_number}
            >
              {creatingSender ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Sender
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Tabs>
  );
}
